import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, HelpCircle, CheckCircle, AlertTriangle, Send, X, BookOpen } from 'lucide-react';

const defaultScenario = {
  id: 'default-qna-1',
  scenario: 'You receive an email from "support@your-bank.com" asking you to verify your account details by clicking a link. The link points to "your-bank.security-update.com".',
  question: 'What is the most appropriate action to take?',
  choices: {
    A: 'Click the link and enter your details as requested.',
    B: 'Reply to the email with your account information.',
    C: 'Delete the email and ignore it.',
    D: 'Go to your bank\'s official website directly and check for any alerts.'
  },
  answer: 'D',
  explanation: 'The email uses a suspicious URL ("your-bank.security-update.com") designed to mimic the real one. This is a classic phishing tactic. The safest action is always to navigate to sensitive websites directly, rather than clicking on links in unsolicited emails.',
  indicators: [
    "The sender's email address domain looks plausible but should be verified.",
    "The link URL is not the official bank domain.",
    "The email creates a sense of urgency to bypass critical thinking."
  ],
  feedback: {
    A: 'Incorrect. Clicking links in unsolicited emails is dangerous. The URL is a phishing attempt.',
    B: 'Incorrect. Never send sensitive information via email.',
    C: 'Ignoring it is better than complying, but it doesn\'t verify if there is a real issue. The best action is to check directly.',
    D: 'Correct! Always verify requests by going directly to the official source, not through links in emails.'
  }
};

const VideoQnASimulator = ({ scenario = defaultScenario, onComplete, challenge }) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasStarted, setHasStarted] = useState(false); // <-- NEW STATE for video gating
  const navigate = useNavigate();

  const activeScenario = scenario && scenario.question ? scenario : defaultScenario;
  const isCorrect = selectedAnswer === scenario.answer;

  const handleSelectAnswer = (choiceKey) => {
    if (isSubmitted) return;
    setSelectedAnswer(choiceKey);
  };

  const handleSubmit = () => {
    if (!selectedAnswer) return;
    setIsSubmitted(true);
    setShowFeedbackModal(true);
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    if (onComplete && typeof onComplete === 'function') {
      onComplete({
        scenarioId: activeScenario.id,
        selectedAnswer: selectedAnswer,
        isCorrect: selectedAnswer === scenario.answer,
      });
    }
  };

  const handleLearnMore = () => {
    const meta = challenge?.metadata;

    // Support older challenges where metadata was a plain URL string
    if (typeof meta === 'string' && meta.startsWith('http')) {
      window.open(meta, '_blank', 'noopener,noreferrer');
      return;
    }

    // Support newer JSON metadata objects with explicit URLs
    if (meta && typeof meta === 'object') {
      const url =
        meta.learnMoreUrl ||
        meta.learn_more_url ||
        meta.docUrl ||
        meta.doc_url ||
        meta.url;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // Fallback to arena-based navigation
    const arenaId = activeScenario?.arenaId || 'phishing_social_engineering';
    if (arenaId) {
      navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };

  const getChoiceBorderColor = (key) => {
    if (!isSubmitted) {
      return selectedAnswer === key
        ? 'border-blue-500 ring-2 ring-blue-200'
        : 'border-gray-600 hover:border-cyan-400';
    }
    if (key === activeScenario.answer) return 'border-green-500 bg-green-50 ring-2 ring-green-200';
    if (key === selectedAnswer && !isCorrect) return 'border-red-500 bg-red-50 ring-2 ring-red-200';
    return 'border-gray-300';
  };

  const renderFeedbackModal = () => {
    if (!showFeedbackModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col p-8 m-4 animate-fade-in-up text-gray-800">
          {/* Header - Fixed */}
          <div className="flex justify-between items-start mb-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              {isCorrect ? (
                <CheckCircle size={28} className="text-green-600" />
              ) : (
                <AlertTriangle size={28} className="text-red-600" />
              )}
              <h2 className={`text-2xl font-bold ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </h2>
            </div>
            <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="text-gray-700 space-y-4">
              <p className="text-base font-medium leading-relaxed">{activeScenario.feedback[selectedAnswer]}</p>

              {activeScenario.explanation && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-bold text-blue-800 mb-2">Detailed Explanation:</h3>
                  <p className="text-gray-700 mt-1 leading-relaxed whitespace-pre-wrap">{activeScenario.explanation}</p>
                </div>
              )}

              {Array.isArray(activeScenario.recommendedActions) && activeScenario.recommendedActions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-bold text-blue-800 mb-2">Here's what you should do:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    {activeScenario.recommendedActions.map((action, idx) => (
                      <li key={idx} className="leading-relaxed">{action}</li>
                    ))}
                  </ol>
                </div>
              )}

              {activeScenario.indicators && activeScenario.indicators.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-bold text-yellow-800 flex items-center mb-2">
                    <AlertTriangle size={16} className="mr-2" />
                    Key Indicators to Notice:
                  </h3>
                  <ul className="list-disc list-inside space-y-1.5 mt-2 text-gray-700 text-sm pl-2">
                    {activeScenario.indicators.map((indicator, index) => <li key={index} className="leading-relaxed">{indicator}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="mt-8 flex justify-end items-center gap-4 flex-shrink-0 border-t border-gray-200 pt-4">
            <button
              onClick={handleLearnMore}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} />
              Learn More
            </button>
            <button
              onClick={handleCloseFeedbackModal}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Derive potential video URL from challenge metadata, supporting both
  // legacy string metadata and newer JSON metadata objects.
  const meta = challenge?.metadata;
  const videoUrl =
    typeof meta === 'string'
      ? meta
      : meta && typeof meta === 'object'
        ? meta.videoUrl || meta.video_url || meta.video || null
        : null;

  // --- NEW: Render video before Q&A starts ---
  if (!hasStarted && typeof videoUrl === 'string' && videoUrl.match(/\.(mp4|webm|ogg)$/i)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-gray-900 text-white p-4 sm:p-6">
        <div className="max-w-4xl w-full">
          <video
            src={videoUrl}
            controls
            className="w-full rounded-xl shadow-2xl mb-6"
          />
          <button
            onClick={() => setHasStarted(true)}
            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white text-lg font-semibold py-4 rounded-xl transition-all duration-300 shadow-[0_0_15px_#0891b2] hover:shadow-[0_0_25px_#0891b2]"
          >
            Start Q&A
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 font-sans bg-gray-900">
      <div className="w-full max-w-6xl mx-auto text-white flex flex-col">

        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6 shadow-md">
          <h2 className="font-bold text-xl text-blue-900 mb-2">Scenario</h2>
          <p className="text-gray-700 leading-relaxed">{activeScenario.scenario}</p>
        </div>

        <div className="mb-6 flex items-start space-x-4">
          <HelpCircle className="text-gray-400 w-7 h-7 flex-shrink-0 mt-1" />
          <h3 className="text-2xl font-semibold text-white flex-1">{activeScenario.question}</h3>
        </div>

        <div className="space-y-4">
          {Object.entries(activeScenario.choices).map(([key, value]) => (
            <button
              key={key}
              onClick={() => handleSelectAnswer(key)}
              disabled={isSubmitted}
              className={`w-full text-left p-5 border rounded-xl transition-all duration-200 flex items-start space-x-4 ${getChoiceBorderColor(key)} ${!isSubmitted ? 'cursor-pointer hover:shadow-[0_0_15px_#06b6d4]' : 'cursor-not-allowed'}`}
            >
              <div className="flex-shrink-0 w-7 h-7 bg-gray-200 text-black font-bold text-sm rounded-full flex items-center justify-center">
                {key}
              </div>
              <span className="flex-1 text-gray-300 text-lg">{value}</span>
            </button>
          ))}
        </div>

        {!isSubmitted && (
          <div className="mt-6">
            <button
              onClick={handleSubmit}
              disabled={!selectedAnswer}
              className={`
                w-full flex items-center justify-center px-10 py-4 text-white font-semibold rounded-xl transition-all duration-300 text-lg
                ${!selectedAnswer
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-cyan-600 hover:bg-cyan-500 shadow-[0_0_15px_#0891b2] hover:shadow-[0_0_25px_#0891b2]'}
              `}
            >
              <Send size={20} className="mr-2" />
              Submit Answer
            </button>
          </div>
        )}

      </div>
      {renderFeedbackModal()}
    </div>
  );
};

export default VideoQnASimulator;